import * as XLSX from 'xlsx';

function decodeMonth(v: any): string | null {
  if (!v) return null;
  if (v instanceof Date) {
    return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}`;
  }
  if (typeof v === 'number' && v > 1000) {
    const date = new Date((v - 25569) * 86400 * 1000);
    const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    return `${utcDate.getFullYear()}-${String(utcDate.getMonth() + 1).padStart(2, '0')}`;
  }
  if (typeof v === 'string') {
    const s = v.trim();
    if (!s) return null;
    const parts = s.split(/[-/]/);
    if (parts.length >= 2) {
      if (parts[0].length === 4) {
        return `${parts[0]}-${parts[1].padStart(2, '0')}`;
      } else if (parts[1].length === 4) {
        return `${parts[1]}-${parts[0].padStart(2, '0')}`;
      } else if (parts[2] && parts[2].length === 4) {
        return `${parts[2]}-${parts[1].padStart(2, '0')}`;
      }
    }
  }
  return null;
}

function classifyAccountCode(accountCode: string | number): [string, string] {
  const code = String(accountCode).trim();
  if (!code || !/^\d+$/.test(code)) {
    return ['UNMAPPED', 'UNMAPPED'];
  }

  const prefix3 = code.slice(0, 3);
  const prefix2 = code.slice(0, 2);

  if (code.startsWith('101')) return ['REVENUE', 'Interest Income'];
  if (code.startsWith('102')) return ['REVENUE', 'Admin Fee'];
  if (code.startsWith('103')) return ['REVENUE', 'Gain Lelang'];
  if (code.startsWith('104')) return ['REVENUE', 'Late Fee'];
  if (code.startsWith('105')) return ['REVENUE', 'Other Interest'];
  if (code.startsWith('106')) return ['REVENUE', 'Accrued Revenue'];

  if (code.startsWith('11101')) return ['COGS', 'Repair Lelang'];
  if (code.startsWith('11102')) return ['COGS', 'Freight In'];
  if (code.startsWith('11103')) return ['COGS', 'Bunga'];
  if (prefix3 === '111') return ['COGS', 'Other Direct'];

  if (prefix3 === '121' || prefix3 === '122' || prefix3 === '123' || prefix3 === '124' || prefix3 === '125') {
    return ['OPEX', 'Personnel'];
  }
  if (prefix3 === '126') return ['OPEX', 'Office & Ops'];
  if (prefix3 === '127') return ['OPEX', 'Depresiasi'];
  if (prefix3 === '128') return ['OPEX', 'Travel'];
  if (prefix2 === '12') return ['OPEX', 'Other OpEx'];

  if (prefix2 === '13' || prefix2 === '14') return ['OTHER', 'Other Income/Expense'];
  if (code.startsWith('151')) return ['OTHER', 'Other Income'];
  if (code.startsWith('152')) return ['OTHER', 'Other Expenses'];
  if (prefix2 === '15') return ['OTHER', 'Other Income/Expense'];

  if (prefix3 === '211' || prefix3 === '212' || prefix3 === '213') return ['ASSET_CURRENT', 'Cash & Bank'];
  if (prefix3 === '214') return ['ASSET_CURRENT', 'Receivables'];
  if (prefix3 === '215') return ['ASSET_CURRENT', 'Other Receivables'];
  if (prefix3 === '216') return ['ASSET_CURRENT', 'Inventory'];
  if (prefix3 === '217' || prefix3 === '218') return ['ASSET_CURRENT', 'Prepaid & Advances'];
  if (prefix3 === '219') return ['ASSET_CURRENT', 'Cash & Bank'];
  if (prefix3 === '221' || prefix3 === '222') return ['ASSET_FIXED', 'Fixed Assets Gross'];
  if (prefix3 === '223') return ['ASSET_FIXED', 'Accum Depreciation'];
  if (prefix3 === '224') return ['ASSET_OTHER', 'Other Assets'];
  if (prefix3 === '225') return ['ASSET_OTHER', 'Deferred Tax'];

  if (prefix3 === '411' || prefix3 === '412' || prefix3 === '413') return ['LIAB_CURRENT', 'Payables'];
  if (prefix3 === '421' || prefix3 === '420') return ['LIAB_CURRENT', 'Accrued Tax/BPJS'];
  if (prefix3 === '431' || prefix3 === '430') return ['LIAB_CURRENT', 'Accrued Salary'];
  if (prefix3 === '441' || prefix3 === '440') return ['LIAB_CURRENT', 'Accrued Expenses'];
  if (prefix3 === '451' || prefix3 === '450') return ['LIAB_LONGTERM', 'Long Term Liabilities'];

  if (code.startsWith('30000')) return ['EQUITY', 'Opening Balance Equity'];
  if (prefix2 === '30') return ['EQUITY', 'Equity'];
  if (prefix2 === '31' || prefix2 === '32' || prefix2 === '33') return ['EQUITY', 'Retained Earnings'];

  return ['UNMAPPED', 'UNMAPPED'];
}

function classifyBsAccountByName(accountName: string): [string, string] {
  const name = String(accountName).toLowerCase().trim();
  const bankKeywords = ['cash', 'bank', 'petty cash', 'bca', 'mandiri', 'flip', 'cimb', 'tunai', 'kas'];
  if (bankKeywords.some(x => x !== 'kas' && name.includes(x)) || name.split(/\s+/).includes('kas')) {
    return ['ASSET_CURRENT', 'Cash & Bank'];
  }
  if (name.includes('receivable') || name.includes('piutang') || name.split(/\s+/).includes('ar') || name.includes('a/r')) {
    return ['ASSET_CURRENT', 'Receivables'];
  }
  if (name.includes('prepaid') || name.includes('advance') || name.includes('dibayar dimuka') || name.includes('prepayment')) {
    return ['ASSET_CURRENT', 'Prepaid & Advances'];
  }
  if (name.includes('inventory') || name.includes('persediaan')) {
    return ['ASSET_CURRENT', 'Inventory'];
  }
  const fixedAssetKeywords = [
    'property', 'equipment', 'machinery', 'vehicle', 'furniture', 'building',
    'software', 'aset tetap', 'mesin', 'kendaraan', 'laptop',
    'air conditioner', 'brankas', 'office inventaris', 'inventaris kantor',
    'computer', 'generator', 'gudang', 'warehouse'
  ];
  if (fixedAssetKeywords.some(x => name.includes(x)) || name.split(/\s+/).includes('ac')) {
    if (!name.includes('depreciat') && !name.includes('accum') && !name.includes('akumulasi')) {
      return ['ASSET_FIXED', 'Fixed Assets Gross'];
    }
  }
  if (name.includes('depreciat') || name.includes('accum') || name.includes('akumulasi')) {
    return ['ASSET_FIXED', 'Accum Depreciation'];
  }
  if (name.includes('deferred') || (name.includes('tax') && name.includes('asset')) || name.includes('pajak tangguhan')) {
    return ['ASSET_OTHER', 'Deferred Tax'];
  }
  if (name.includes('other asset') || name.includes('aset lainnya')) {
    return ['ASSET_OTHER', 'Other Assets'];
  }
  if (name.includes('payable') || name.includes('hutang') || name.includes('utang')) {
    if (!name.includes('long term') && !name.includes('jangka panjang')) {
      return ['LIAB_CURRENT', 'Payables'];
    }
  }
  if (name.includes('accrued') || name.includes('terutang')) {
    if (name.includes('tax') || name.includes('bpjs') || name.includes('pph') || name.includes('vat') || name.includes('pajak')) {
      return ['LIAB_CURRENT', 'Accrued Tax/BPJS'];
    }
    if (name.includes('salary') || name.includes('gaji') || name.includes('wage')) {
      return ['LIAB_CURRENT', 'Accrued Salary'];
    }
    return ['LIAB_CURRENT', 'Accrued Expenses'];
  }
  if (name.includes('long term') || name.includes('jangka panjang')) {
    return ['LIAB_LONGTERM', 'Long Term Liabilities'];
  }
  if (name.includes('equity') || name.includes('modal') || name.includes('capital')) {
    return ['EQUITY', 'Equity'];
  }
  if (name.includes('retained earning') || name.includes('laba ditahan')) {
    return ['EQUITY', 'Retained Earnings'];
  }
  if (name.includes('opening balance') || name.includes('saldo awal') || name.includes('saldo pembukaan')) {
    return ['EQUITY', 'Opening Balance Equity'];
  }
  return ['UNMAPPED', 'UNMAPPED'];
}

function getCellValue(sheet: XLSX.WorkSheet, r: number, c: number): any {
  const cell = sheet[XLSX.utils.encode_cell({ r, c })];
  return cell ? cell.v : null;
}

function readRowAligned(sheet: XLSX.WorkSheet, rowIdx: number, monthsTarget: string[], anchorRowIdx: number): number[] {
  const range = XLSX.utils.decode_range(sheet['!ref'] || '');
  const out = Array(monthsTarget.length).fill(0.0);
  const targetIdx: Record<string, number> = {};
  monthsTarget.forEach((m, i) => { targetIdx[m] = i; });

  for (let c = 2; c <= range.e.c; ++c) {
    const anchorVal = getCellValue(sheet, anchorRowIdx, c);
    if (anchorVal === null || anchorVal === undefined) break;
    const mk = decodeMonth(anchorVal);
    if (!mk) break;
    if (targetIdx[mk] !== undefined) {
      const val = getCellValue(sheet, rowIdx - 1, c);
      out[targetIdx[mk]] = Number(val || 0.0);
    }
  }
  return out;
}

function findAnchorRow(sheet: XLSX.WorkSheet): number {
  const range = XLSX.utils.decode_range(sheet['!ref'] || '');
  for (let r = range.s.r; r <= range.e.r; ++r) {
    const val = getCellValue(sheet, r, 0);
    if (val && String(val).trim().toLowerCase() === 'month serial') {
      return r;
    }
  }
  throw new Error("Anchor row 'Month Serial' not found");
}

function readAnchorMonths(sheet: XLSX.WorkSheet): string[] {
  const anchorRow = findAnchorRow(sheet);
  const range = XLSX.utils.decode_range(sheet['!ref'] || '');
  const months: string[] = [];
  for (let c = 2; c <= range.e.c; ++c) {
    const val = getCellValue(sheet, anchorRow, c);
    const mk = decodeMonth(val);
    if (!mk) break;
    months.push(mk);
  }
  return months;
}

function zeros(len: number): number[] {
  return Array(len).fill(0.0);
}

function sum(lists: number[][], len: number): number[] {
  if (lists.length === 0) return zeros(len);
  const res = zeros(len);
  for (const list of lists) {
    for (let i = 0; i < len; i++) {
      res[i] += list[i] || 0.0;
    }
  }
  return res;
}

function neg(list: number[]): number[] {
  return list.map(v => -v);
}

/**
 * Parse a number from a CSV cell. Google CSV export formats numbers with thousand
 * separators and uses a dash for empty cells, e.g. "  1,000,000 ", "- 1,000,000 ",
 * "  -   ", "(1,234)". xlsx cells give raw numbers, so this is only for the CSV path.
 */
function parseGlNum(v: any): number {
  if (v === null || v === undefined) return 0;
  let s = String(v).trim();
  if (s === '' || s === '-') return 0;
  let negative = false;
  if (s.startsWith('(') && s.endsWith(')')) {
    negative = true;
    s = s.slice(1, -1);
  }
  s = s.replace(/,/g, '').replace(/\s/g, '');
  if (s === '' || s === '-') return 0;
  const n = Number(s);
  if (!isFinite(n)) return 0;
  return negative ? -n : n;
}

/** RFC-4180-ish CSV parser: handles quoted fields, escaped quotes, embedded newlines, CRLF. */
function parseCsvText(text: string): string[][] {
  const rows: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else inQuotes = false;
      } else {
        field += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        row.push(field); field = '';
      } else if (ch === '\n') {
        row.push(field); rows.push(row); row = []; field = '';
      } else if (ch === '\r') {
        // ignore; handled on the following \n
      } else {
        field += ch;
      }
    }
  }
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  return rows;
}

function isBlank(v: any): boolean {
  return v === null || v === undefined || String(v).trim() === '';
}

function buildForArea(cubeArea: any, detailCubeArea: any, monthsLen: number) {
  const rev = cubeArea['REVENUE'] || {};
  const cogs = cubeArea['COGS'] || {};
  const opex = cubeArea['OPEX'] || {};
  const other = cubeArea['OTHER'] || {};

  const revenue = {
    interestIncome: neg(rev['Interest Income'] || zeros(monthsLen)),
    adminFee:       neg(rev['Admin Fee'] || zeros(monthsLen)),
    gainLelang:     neg(rev['Gain Lelang'] || zeros(monthsLen)),
    lateFee:        neg(rev['Late Fee'] || zeros(monthsLen)),
    otherInterest:  neg(rev['Other Interest'] || zeros(monthsLen)),
    accruedRevenue: neg(rev['Accrued Revenue'] || zeros(monthsLen)),
    total: [] as number[]
  };
  revenue.total = sum([
    revenue.interestIncome, revenue.adminFee, revenue.gainLelang,
    revenue.lateFee, revenue.otherInterest, revenue.accruedRevenue
  ], monthsLen);

  const cogsOut = {
    repairLelang:    cogs['Repair Lelang'] || zeros(monthsLen),
    freightIn:       cogs['Freight In'] || zeros(monthsLen),
    interestExpense: cogs['Bunga'] || zeros(monthsLen),
    total: [] as number[]
  };
  cogsOut.total = sum([cogsOut.repairLelang, cogsOut.freightIn, cogsOut.interestExpense], monthsLen);

  const grossProfit = revenue.total.map((r, i) => r - cogsOut.total[i]);

  const buildItems = (catName: string) => {
    const items: any[] = [];
    const catDetails = (detailCubeArea['OPEX'] || {})[catName] || {};
    for (const [k, v] of Object.entries(catDetails)) {
      const parts = k.split('|');
      const noAcc = parts[0];
      const name = parts[1] || k;
      items.push({ noAcc, name, values: v });
    }
    items.sort((a, b) => a.noAcc.localeCompare(b.noAcc));
    return items;
  };

  const opexOut = {
    personnel:    { total: opex['Personnel'] || zeros(monthsLen), items: buildItems('Personnel') },
    officeOps:    { total: opex['Office & Ops'] || zeros(monthsLen), items: buildItems('Office & Ops') },
    depreciation: { total: opex['Depresiasi'] || zeros(monthsLen), items: buildItems('Depresiasi') },
    travel:       { total: opex['Travel'] || zeros(monthsLen), items: buildItems('Travel') },
    otherOpex:    { total: opex['Other OpEx'] || zeros(monthsLen), items: buildItems('Other OpEx') },
    total: [] as number[]
  };
  opexOut.total = sum([
    opexOut.personnel.total, opexOut.officeOps.total,
    opexOut.depreciation.total, opexOut.travel.total, opexOut.otherOpex.total
  ], monthsLen);

  const operatingProfit = grossProfit.map((g, i) => g - opexOut.total[i]);

  const otherIncome = neg(other['Other Income'] || zeros(monthsLen));
  const otherExp = other['Other Expenses'] || zeros(monthsLen);
  const otherIe = otherIncome.map((inc, i) => inc - otherExp[i]);

  const netProfit = operatingProfit.map((op, i) => op + otherIe[i]);

  return {
    revenue,
    cogs: cogsOut,
    grossProfit,
    opex: opexOut,
    operatingProfit,
    otherIncomeExpense: otherIe,
    netProfit
  };
}

export interface SyncParseResult {
  compiledData: any;
  transactionsBatch: any[];
}

interface GlCubes {
  cube: any;
  detailCube: any;
  branchCube: any;
  transactionsBatch: any[];
}

/**
 * Build the aggregation cubes + transaction list from GL rows. The row source is
 * abstracted via `cellAt(r, c)` and `parseAmount` so the same logic serves both the
 * xlsx accessor and the CSV accessor. Data rows start at index 2 (rows 0–1 are headers).
 */
function ingestGl(
  cellAt: (r: number, c: number) => any,
  rowCount: number,
  monthIdxMap: Record<string, number>,
  monthsLen: number,
  branchToArea: Record<string, string>,
  entity: string,
  parseAmount: (v: any) => number
): GlCubes {
  const cube: any = {};
  const detailCube: any = {};
  const branchCube: any = {};

  const initAreaCube = (a: string) => {
    if (!cube[a]) cube[a] = {};
    if (!detailCube[a]) detailCube[a] = {};
  };
  const addToCube = (cArea: string, section: string, category: string, mIdx: number, val: number) => {
    initAreaCube(cArea);
    if (!cube[cArea][section]) cube[cArea][section] = {};
    if (!cube[cArea][section][category]) cube[cArea][section][category] = zeros(monthsLen);
    cube[cArea][section][category][mIdx] += val;
  };
  const addToDetailCube = (cArea: string, section: string, category: string, dkey: string, mIdx: number, val: number) => {
    initAreaCube(cArea);
    if (!detailCube[cArea][section]) detailCube[cArea][section] = {};
    if (!detailCube[cArea][section][category]) detailCube[cArea][section][category] = {};
    if (!detailCube[cArea][section][category][dkey]) detailCube[cArea][section][category][dkey] = zeros(monthsLen);
    detailCube[cArea][section][category][dkey][mIdx] += val;
  };

  const transactionsBatch: any[] = [];
  let consecutiveEmptyCount = 0;

  for (let r = 2; r < rowCount; ++r) {
    const moVal = cellAt(r, 3);   // mo
    const account = cellAt(r, 5); // No acc

    if (isBlank(moVal) && isBlank(account)) {
      consecutiveEmptyCount++;
      if (consecutiveEmptyCount > 50) break;
      continue;
    }
    consecutiveEmptyCount = 0;

    const mk = decodeMonth(moVal);
    if (!mk || monthIdxMap[mk] === undefined) continue;

    const midx = monthIdxMap[mk];
    if (isBlank(account)) continue;

    let criteria = cellAt(r, 4); // Criteria
    if (!criteria) {
      const accStr = String(account).trim();
      criteria = (accStr && ['2', '3', '4'].includes(accStr[0])) ? 'BS' : 'PL';
    } else {
      criteria = String(criteria).trim();
    }

    const accountName = !isBlank(cellAt(r, 6)) ? String(cellAt(r, 6)).trim() : '';
    const debit = parseAmount(cellAt(r, 9));
    const credit = parseAmount(cellAt(r, 10));
    const net = debit - credit;
    const branch = !isBlank(cellAt(r, 8)) ? String(cellAt(r, 8)).trim() : '';
    const area = branchToArea[branch] || 'UNKNOWN';

    let section = 'UNMAPPED';
    let category = 'UNMAPPED';

    if (criteria === 'BS') {
      const res = classifyAccountCode(account);
      section = res[0];
      category = res[1];
      if (section === 'UNMAPPED') {
        const resName = classifyBsAccountByName(accountName);
        section = resName[0];
        category = resName[1];
      }
    } else {
      const res = classifyAccountCode(account);
      section = res[0];
      category = res[1];
    }

    addToCube(area, section, category, midx, net);
    const detailKey = `${String(account).trim()}|${accountName}`;
    addToDetailCube(area, section, category, detailKey, midx, net);

    if (area !== 'UNKNOWN') {
      if (!branchCube[branch]) branchCube[branch] = {};
      if (!branchCube[branch][section]) branchCube[branch][section] = {};
      if (!branchCube[branch][section][category]) branchCube[branch][section][category] = zeros(monthsLen);
      branchCube[branch][section][category][midx] += net;
    }

    transactionsBatch.push({
      entity,
      mo: mk,
      acc_code: String(account).trim(),
      acc_name: accountName,
      description: !isBlank(cellAt(r, 7)) ? String(cellAt(r, 7)).trim() : '',
      branch,
      area,
      debit,
      credit,
      amount: net
    });
  }

  return { cube, detailCube, branchCube, transactionsBatch };
}

/**
 * Steps 4–10: from the GL cubes + the pre-aggregated workbook sheets, assemble the
 * compiled dashboard payload. Identical for both the xlsx-GL and CSV-GL paths.
 */
function assembleCompiledData(
  workbook: XLSX.WorkBook,
  MONTHS_STR: string[],
  monthsMeta: any[],
  monthsLen: number,
  branches: any[],
  branchToArea: Record<string, string>,
  glc: GlCubes
): SyncParseResult {
  const { cube, detailCube, branchCube, transactionsBatch } = glc;

  // 4. Kompilasi ALL Area (total Depok + Bogor + Bekasi + UNKNOWN)
  const initAreaCube = (a: string) => {
    if (!cube[a]) cube[a] = {};
    if (!detailCube[a]) detailCube[a] = {};
  };
  const allSections = new Set<string>();
  Object.values(cube).forEach((secMap: any) => {
    Object.keys(secMap).forEach(sec => allSections.add(sec));
  });

  allSections.forEach(section => {
    const allCategories = new Set<string>();
    Object.values(cube).forEach((secMap: any) => {
      if (secMap[section]) {
        Object.keys(secMap[section]).forEach(cat => allCategories.add(cat));
      }
    });

    allCategories.forEach(category => {
      const totals = zeros(monthsLen);
      for (const [areaName, secMap] of Object.entries(cube)) {
        if (areaName === 'ALL') continue;
        const vals = (secMap as any)[section]?.[category] || zeros(monthsLen);
        for (let i = 0; i < monthsLen; i++) {
          totals[i] += vals[i] || 0.0;
        }
      }
      initAreaCube('ALL');
      if (!cube['ALL'][section]) cube['ALL'][section] = {};
      cube['ALL'][section][category] = totals;

      const allDetailKeys = new Set<string>();
      Object.values(detailCube).forEach((secMap: any) => {
        if (secMap[section]?.[category]) {
          Object.keys(secMap[section][category]).forEach(dkey => allDetailKeys.add(dkey));
        }
      });

      allDetailKeys.forEach(dkey => {
        const dTotals = zeros(monthsLen);
        for (const [areaName, secMap] of Object.entries(detailCube)) {
          if (areaName === 'ALL') continue;
          const dVals = (secMap as any)[section]?.[category]?.[dkey] || zeros(monthsLen);
          for (let i = 0; i < monthsLen; i++) {
            dTotals[i] += dVals[i] || 0.0;
          }
        }
        initAreaCube('ALL');
        if (!detailCube['ALL'][section]) detailCube['ALL'][section] = {};
        if (!detailCube['ALL'][section][category]) detailCube['ALL'][section][category] = {};
        detailCube['ALL'][section][category][dkey] = dTotals;
      });
    });
  });

  // 5. Build Profit & Loss
  const plByArea: Record<string, any> = {};
  const plBranches: Record<string, any> = {};

  const areasList = ['ALL', 'Depok', 'Bogor', 'Bekasi'];
  areasList.forEach(a => {
    plByArea[a] = buildForArea(cube[a] || {}, detailCube[a] || {}, monthsLen);
  });

  for (const [bCode, bCube] of Object.entries(branchCube)) {
    const bDetail = {};
    plBranches[bCode] = buildForArea(bCube, bDetail, monthsLen);
  }

  // 6. Baca Neraca (Balance Sheet) Lembar Pre-aggregated
  const bsSheetName = workbook.SheetNames.find(s => s.toLowerCase() === 'balance sheet' || s.toLowerCase() === 'excel balance sheet');
  if (!bsSheetName) {
    throw new Error("Lembar 'Balance Sheet' tidak ditemukan.");
  }
  const bsSheet = workbook.Sheets[bsSheetName];
  const bsAnchorIdx = findAnchorRow(bsSheet);
  const bsSheetMonths = readAnchorMonths(bsSheet);

  const balanceSheetAll = {
    currentAssets: {
      cashBank: readRowAligned(bsSheet, 15, MONTHS_STR, bsAnchorIdx),
      receivables: readRowAligned(bsSheet, 20, MONTHS_STR, bsAnchorIdx),
      prepaidAdvances: readRowAligned(bsSheet, 26, MONTHS_STR, bsAnchorIdx),
      total: readRowAligned(bsSheet, 28, MONTHS_STR, bsAnchorIdx),
    },
    fixedAssets: {
      gross: readRowAligned(bsSheet, 31, MONTHS_STR, bsAnchorIdx),
      accumDepreciation: readRowAligned(bsSheet, 32, MONTHS_STR, bsAnchorIdx),
      net: readRowAligned(bsSheet, 33, MONTHS_STR, bsAnchorIdx),
    },
    otherAssets: {
      deferredTax: readRowAligned(bsSheet, 36, MONTHS_STR, bsAnchorIdx),
    },
    totalAssets: readRowAligned(bsSheet, 38, MONTHS_STR, bsAnchorIdx),
    currentLiabilities: {
      payables: readRowAligned(bsSheet, 43, MONTHS_STR, bsAnchorIdx),
      accruedTax: readRowAligned(bsSheet, 44, MONTHS_STR, bsAnchorIdx),
      accruedBPJS: readRowAligned(bsSheet, 45, MONTHS_STR, bsAnchorIdx),
      accruedSalary: readRowAligned(bsSheet, 46, MONTHS_STR, bsAnchorIdx),
      accruedExpenses: readRowAligned(bsSheet, 47, MONTHS_STR, bsAnchorIdx),
      total: readRowAligned(bsSheet, 48, MONTHS_STR, bsAnchorIdx),
    },
    longTermLiabilities: readRowAligned(bsSheet, 51, MONTHS_STR, bsAnchorIdx),
    totalLiabilities: readRowAligned(bsSheet, 53, MONTHS_STR, bsAnchorIdx),
    equity: {
      openingBalance: readRowAligned(bsSheet, 56, MONTHS_STR, bsAnchorIdx),
      retainedEarnings: readRowAligned(bsSheet, 57, MONTHS_STR, bsAnchorIdx),
      total: readRowAligned(bsSheet, 58, MONTHS_STR, bsAnchorIdx),
    },
  };

  // Rollforward Balance Sheet jika ada bulan di MONTHS_STR yang tidak ada di bsSheetMonths
  const sheetMonthsSet = new Set(bsSheetMonths);
  for (let i = 0; i < monthsLen; i++) {
    const m = MONTHS_STR[i];
    if (!sheetMonthsSet.has(m) && i > 0) {
      const netProfit = plByArea['ALL'].netProfit[i] || 0.0;
      const bsCube = cube['ALL'] || {};

      const ac = bsCube['ASSET_CURRENT'] || {};
      const af = bsCube['ASSET_FIXED'] || {};
      const ao = bsCube['ASSET_OTHER'] || {};
      const lc = bsCube['LIAB_CURRENT'] || {};
      const ll = bsCube['LIAB_LONGTERM'] || {};
      const eq = bsCube['EQUITY'] || {};

      balanceSheetAll.currentAssets.cashBank[i] = balanceSheetAll.currentAssets.cashBank[i-1] + (ac['Cash & Bank']?.[i] || 0.0);
      balanceSheetAll.currentAssets.receivables[i] = balanceSheetAll.currentAssets.receivables[i-1] + (ac['Receivables']?.[i] || 0.0) + (ac['Other Receivables']?.[i] || 0.0);
      balanceSheetAll.currentAssets.prepaidAdvances[i] = balanceSheetAll.currentAssets.prepaidAdvances[i-1] + (ac['Prepaid & Advances']?.[i] || 0.0) + (ac['Inventory']?.[i] || 0.0);
      balanceSheetAll.currentAssets.total[i] = balanceSheetAll.currentAssets.cashBank[i] + balanceSheetAll.currentAssets.receivables[i] + balanceSheetAll.currentAssets.prepaidAdvances[i];

      balanceSheetAll.fixedAssets.gross[i] = balanceSheetAll.fixedAssets.gross[i-1] + (af['Fixed Assets Gross']?.[i] || 0.0);
      balanceSheetAll.fixedAssets.accumDepreciation[i] = balanceSheetAll.fixedAssets.accumDepreciation[i-1] + (af['Accum Depreciation']?.[i] || 0.0);
      balanceSheetAll.fixedAssets.net[i] = balanceSheetAll.fixedAssets.gross[i] + balanceSheetAll.fixedAssets.accumDepreciation[i];

      balanceSheetAll.otherAssets.deferredTax[i] = balanceSheetAll.otherAssets.deferredTax[i-1] + (ao['Deferred Tax']?.[i] || 0.0) + (ao['Other Assets']?.[i] || 0.0);
      balanceSheetAll.totalAssets[i] = balanceSheetAll.currentAssets.total[i] + balanceSheetAll.fixedAssets.net[i] + balanceSheetAll.otherAssets.deferredTax[i];

      balanceSheetAll.currentLiabilities.payables[i] = balanceSheetAll.currentLiabilities.payables[i-1] + (lc['Payables']?.[i] || 0.0);
      balanceSheetAll.currentLiabilities.accruedTax[i] = balanceSheetAll.currentLiabilities.accruedTax[i-1] + (lc['Accrued Tax/BPJS']?.[i] || 0.0);
      balanceSheetAll.currentLiabilities.accruedBPJS[i] = balanceSheetAll.currentLiabilities.accruedBPJS[i-1];
      balanceSheetAll.currentLiabilities.accruedSalary[i] = balanceSheetAll.currentLiabilities.accruedSalary[i-1] + (lc['Accrued Salary']?.[i] || 0.0);
      balanceSheetAll.currentLiabilities.accruedExpenses[i] = balanceSheetAll.currentLiabilities.accruedExpenses[i-1] + (lc['Accrued Expenses']?.[i] || 0.0);
      balanceSheetAll.currentLiabilities.total[i] = balanceSheetAll.currentLiabilities.payables[i] + balanceSheetAll.currentLiabilities.accruedTax[i] + balanceSheetAll.currentLiabilities.accruedBPJS[i] + balanceSheetAll.currentLiabilities.accruedSalary[i] + balanceSheetAll.currentLiabilities.accruedExpenses[i];

      balanceSheetAll.longTermLiabilities[i] = balanceSheetAll.longTermLiabilities[i-1] + (ll['Long Term Liabilities']?.[i] || 0.0);
      balanceSheetAll.totalLiabilities[i] = balanceSheetAll.currentLiabilities.total[i] + balanceSheetAll.longTermLiabilities[i];

      balanceSheetAll.equity.openingBalance[i] = balanceSheetAll.equity.openingBalance[i-1] + (eq['Opening Balance Equity']?.[i] || 0.0) + (eq['Equity']?.[i] || 0.0);
      balanceSheetAll.equity.retainedEarnings[i] = balanceSheetAll.equity.retainedEarnings[i-1] - netProfit;
      balanceSheetAll.equity.total[i] = balanceSheetAll.equity.openingBalance[i] + balanceSheetAll.equity.retainedEarnings[i];
    }
  }

  // 7. Baca Cash Flow Lembar Pre-aggregated
  const cfSheetName = workbook.SheetNames.find(s => s.toLowerCase() === 'cash flow' || s.toLowerCase() === 'excel cash flow');
  if (!cfSheetName) {
    throw new Error("Lembar 'Cash Flow' tidak ditemukan.");
  }
  const cfSheet = workbook.Sheets[cfSheetName];
  const cfAnchorIdx = findAnchorRow(cfSheet);
  const cfSheetMonths = readAnchorMonths(cfSheet);

  const cashFlowAll = {
    operating: {
      netPL: readRowAligned(cfSheet, 7, MONTHS_STR, cfAnchorIdx),
      depreciation: readRowAligned(cfSheet, 8, MONTHS_STR, cfAnchorIdx),
      chgReceivables: readRowAligned(cfSheet, 9, MONTHS_STR, cfAnchorIdx),
      chgOtherReceivables: readRowAligned(cfSheet, 10, MONTHS_STR, cfAnchorIdx),
      chgPrepaid: readRowAligned(cfSheet, 11, MONTHS_STR, cfAnchorIdx),
      chgOperationalAdvances: readRowAligned(cfSheet, 12, MONTHS_STR, cfAnchorIdx),
      chgPayables: readRowAligned(cfSheet, 13, MONTHS_STR, cfAnchorIdx),
      chgAccruedTaxBPJS: readRowAligned(cfSheet, 14, MONTHS_STR, cfAnchorIdx),
      chgAccruedSalary: readRowAligned(cfSheet, 15, MONTHS_STR, cfAnchorIdx),
      chgAccruedExpenses: readRowAligned(cfSheet, 16, MONTHS_STR, cfAnchorIdx),
      total: readRowAligned(cfSheet, 18, MONTHS_STR, cfAnchorIdx),
    },
    investing: {
      chgFixedAssets: readRowAligned(cfSheet, 20, MONTHS_STR, cfAnchorIdx),
      total: readRowAligned(cfSheet, 22, MONTHS_STR, cfAnchorIdx),
    },
    financing: {
      chgLTLiabilities: readRowAligned(cfSheet, 24, MONTHS_STR, cfAnchorIdx),
      chgOpeningEquity: readRowAligned(cfSheet, 25, MONTHS_STR, cfAnchorIdx),
      chgCrossTransfer: readRowAligned(cfSheet, 26, MONTHS_STR, cfAnchorIdx),
      total: readRowAligned(cfSheet, 28, MONTHS_STR, cfAnchorIdx),
    },
    netChange: readRowAligned(cfSheet, 30, MONTHS_STR, cfAnchorIdx),
    openingCash: readRowAligned(cfSheet, 32, MONTHS_STR, cfAnchorIdx),
    closingCash: readRowAligned(cfSheet, 33, MONTHS_STR, cfAnchorIdx),
  };

  // Reconstruct Cash Flow jika ada bulan kosong
  const cfSheetMonthsSet = new Set(cfSheetMonths);
  for (let i = 0; i < monthsLen; i++) {
    const m = MONTHS_STR[i];
    const isEmpty = cashFlowAll.operating.total[i] === 0 && cashFlowAll.investing.total[i] === 0 && cashFlowAll.financing.total[i] === 0;
    if (!cfSheetMonthsSet.has(m) || isEmpty) {
      if (i > 0) {
        cashFlowAll.operating.netPL[i] = plByArea['ALL'].netProfit[i] || 0.0;
        cashFlowAll.operating.depreciation[i] = plByArea['ALL'].opex.depreciation.total[i] || 0.0;
        cashFlowAll.operating.chgReceivables[i] = -(balanceSheetAll.currentAssets.receivables[i] - balanceSheetAll.currentAssets.receivables[i-1]);
        cashFlowAll.operating.chgOtherReceivables[i] = 0.0;
        cashFlowAll.operating.chgPrepaid[i] = -(balanceSheetAll.currentAssets.prepaidAdvances[i] - balanceSheetAll.currentAssets.prepaidAdvances[i-1]);
        cashFlowAll.operating.chgOperationalAdvances[i] = 0.0;
        cashFlowAll.operating.chgPayables[i] = -(balanceSheetAll.currentLiabilities.payables[i] - balanceSheetAll.currentLiabilities.payables[i-1]);
        cashFlowAll.operating.chgAccruedTaxBPJS[i] = -(
          balanceSheetAll.currentLiabilities.accruedTax[i] - balanceSheetAll.currentLiabilities.accruedTax[i-1] +
          balanceSheetAll.currentLiabilities.accruedBPJS[i] - balanceSheetAll.currentLiabilities.accruedBPJS[i-1]
        );
        cashFlowAll.operating.chgAccruedSalary[i] = -(balanceSheetAll.currentLiabilities.accruedSalary[i] - balanceSheetAll.currentLiabilities.accruedSalary[i-1]);
        cashFlowAll.operating.chgAccruedExpenses[i] = -(balanceSheetAll.currentLiabilities.accruedExpenses[i] - balanceSheetAll.currentLiabilities.accruedExpenses[i-1]);

        cashFlowAll.operating.total[i] =
          cashFlowAll.operating.netPL[i] +
          cashFlowAll.operating.depreciation[i] +
          cashFlowAll.operating.chgReceivables[i] +
          cashFlowAll.operating.chgOtherReceivables[i] +
          cashFlowAll.operating.chgPrepaid[i] +
          cashFlowAll.operating.chgOperationalAdvances[i] +
          cashFlowAll.operating.chgPayables[i] +
          cashFlowAll.operating.chgAccruedTaxBPJS[i] +
          cashFlowAll.operating.chgAccruedSalary[i] +
          cashFlowAll.operating.chgAccruedExpenses[i];

        cashFlowAll.investing.chgFixedAssets[i] = -(balanceSheetAll.fixedAssets.gross[i] - balanceSheetAll.fixedAssets.gross[i-1]);
        cashFlowAll.investing.total[i] = cashFlowAll.investing.chgFixedAssets[i];

        cashFlowAll.financing.chgLTLiabilities[i] = -(balanceSheetAll.longTermLiabilities[i] - balanceSheetAll.longTermLiabilities[i-1]);
        cashFlowAll.financing.chgOpeningEquity[i] = -(balanceSheetAll.equity.openingBalance[i] - balanceSheetAll.equity.openingBalance[i-1]);
        cashFlowAll.financing.chgCrossTransfer[i] = 0.0;
        cashFlowAll.financing.total[i] = cashFlowAll.financing.chgLTLiabilities[i] + cashFlowAll.financing.chgOpeningEquity[i];

        cashFlowAll.netChange[i] = cashFlowAll.operating.total[i] + cashFlowAll.investing.total[i] + cashFlowAll.financing.total[i];
        cashFlowAll.openingCash[i] = balanceSheetAll.currentAssets.cashBank[i-1];
        cashFlowAll.closingCash[i] = balanceSheetAll.currentAssets.cashBank[i];
      }
    }
  }

  // 8. Baca AR Report Lembar Pre-aggregated
  const arByBranch: Record<string, number[]> = {};
  let arTotal: number[] = zeros(monthsLen);
  const arSheet = workbook.Sheets['AR Report'];
  if (arSheet) {
    const arAnchorIdx = findAnchorRow(arSheet);
    for (let r = 5; r <= 35; ++r) {
      const label = getCellValue(arSheet, r, 1);
      if (!label || !String(label).includes('Receivable')) continue;

      const parts = String(label).split(' - ');
      if (parts.length < 2) continue;

      const bCode = parts[1].split(' ')[0].trim();
      const vals = readRowAligned(arSheet, r + 1, MONTHS_STR, arAnchorIdx);

      if (arByBranch[bCode]) {
        arByBranch[bCode] = arByBranch[bCode].map((v, i) => v + vals[i]);
      } else {
        arByBranch[bCode] = vals;
      }
    }
    arTotal = sum(Object.values(arByBranch), monthsLen);
  }

  // 9. Kompilasi data ke Neraca Cabang (bsBranch & branchFullBS)
  const bsBranches: Record<string, any> = {};
  const bsFullBranches: Record<string, any> = {};

  for (const [branch, bCube] of Object.entries(branchCube)) {
    const ac = (bCube as any).ASSET_CURRENT || {};
    bsBranches[branch] = {
      cashBank: ac['Cash & Bank'] || zeros(monthsLen),
      receivables: ac['Receivables'] || zeros(monthsLen)
    };

    const af = (bCube as any).ASSET_FIXED || {};
    const ao = (bCube as any).ASSET_OTHER || {};
    const lc = (bCube as any).LIAB_CURRENT || {};
    const ll = (bCube as any).LIAB_LONGTERM || {};
    const eq = (bCube as any).EQUITY || {};

    bsFullBranches[branch] = {
      currentAssets: {
        cashBank: ac['Cash & Bank'] || zeros(monthsLen),
        receivables: ac['Receivables'] || zeros(monthsLen),
        prepaidAdvances: ac['Prepaid & Advances'] || zeros(monthsLen),
        otherReceivables: ac['Other Receivables'] || zeros(monthsLen),
        inventory: ac['Inventory'] || zeros(monthsLen),
        total: sum([
          ac['Cash & Bank'] || zeros(monthsLen),
          ac['Receivables'] || zeros(monthsLen),
          ac['Prepaid & Advances'] || zeros(monthsLen),
          ac['Other Receivables'] || zeros(monthsLen),
          ac['Inventory'] || zeros(monthsLen)
        ], monthsLen)
      },
      fixedAssets: {
        gross: af['Fixed Assets Gross'] || zeros(monthsLen),
        accumDepreciation: af['Accum Depreciation'] || zeros(monthsLen),
        net: sum([
          af['Fixed Assets Gross'] || zeros(monthsLen),
          af['Accum Depreciation'] || zeros(monthsLen)
        ], monthsLen)
      },
      otherAssets: {
        deferredTax: ao['Deferred Tax'] || zeros(monthsLen)
      },
      totalAssets: [] as number[],
      currentLiabilities: {
        payables: lc['Payables'] || zeros(monthsLen),
        accruedTax: lc['Accrued Tax/BPJS'] || zeros(monthsLen),
        accruedBPJS: zeros(monthsLen),
        accruedSalary: lc['Accrued Salary'] || zeros(monthsLen),
        accruedExpenses: lc['Accrued Expenses'] || zeros(monthsLen),
        total: sum([
          lc['Payables'] || zeros(monthsLen),
          lc['Accrued Tax/BPJS'] || zeros(monthsLen),
          lc['Accrued Salary'] || zeros(monthsLen),
          lc['Accrued Expenses'] || zeros(monthsLen)
        ], monthsLen)
      },
      longTermLiabilities: ll['Long Term Liabilities'] || zeros(monthsLen),
      totalLiabilities: [] as number[],
      equity: {
        openingBalance: eq['Opening Balance Equity'] || zeros(monthsLen),
        retainedEarnings: eq['Retained Earnings'] || zeros(monthsLen),
        total: sum([
          eq['Opening Balance Equity'] || zeros(monthsLen),
          eq['Retained Earnings'] || zeros(monthsLen)
        ], monthsLen)
      }
    };

    bsFullBranches[branch].totalAssets = sum([
      bsFullBranches[branch].currentAssets.total,
      bsFullBranches[branch].fixedAssets.net,
      bsFullBranches[branch].otherAssets.deferredTax
    ], monthsLen);

    bsFullBranches[branch].totalLiabilities = sum([
      bsFullBranches[branch].currentLiabilities.total,
      bsFullBranches[branch].longTermLiabilities
    ], monthsLen);
  }

  const unknownBranchesList = Object.keys(branchCube).filter(b => !branchToArea[b]);

  // Tambahkan presets FY
  const fyPresets: Record<string, any> = {};
  const byYear: Record<number, [number, number]> = {};
  MONTHS_STR.forEach((m, i) => {
    const year = parseInt(m.substring(0, 4));
    if (byYear[year] === undefined) {
      byYear[year] = [i, i];
    } else {
      byYear[year][1] = i;
    }
  });

  const sortedYears = Object.keys(byYear).map(Number).sort((a, b) => a - b);
  const lastYear = sortedYears[sortedYears.length - 1];
  const lastMonthOfLastYear = parseInt(MONTHS_STR[byYear[lastYear][1]].split('-')[1]);
  const lastYearIsPartial = lastMonthOfLastYear < 12;

  sortedYears.forEach(year => {
    const [start, end] = byYear[year];
    const isYtd = (year === lastYear && lastYearIsPartial);
    const prefix = isYtd ? 'YTD' : 'FY';
    const key = `${prefix}${year}`;
    fyPresets[key] = { startIdx: start, endIdx: end, label: `${prefix} ${year}` };
  });

  const AREAS = ['ALL', 'Depok', 'Bogor', 'Bekasi'];

  const compiledData = {
    MONTHS: monthsMeta,
    FY_PRESETS: fyPresets,
    AREAS,
    branches,
    profitLoss: plByArea,
    profitLossByBranch: plBranches,
    branchToArea,
    branchFullBS: bsFullBranches,
    bsBranch: bsBranches,
    unknownBranches: unknownBranchesList,
    balanceSheet: {
      ALL: balanceSheetAll,
      Depok: balanceSheetAll,
      Bogor: balanceSheetAll,
      Bekasi: balanceSheetAll
    },
    cashFlow: {
      ALL: cashFlowAll,
      Depok: cashFlowAll,
      Bogor: cashFlowAll,
      Bekasi: cashFlowAll
    },
    accountReceivable: {
      ALL: { byBranch: arByBranch, total: arTotal },
      Depok: { byBranch: arByBranch, total: arTotal },
      Bogor: { byBranch: arByBranch, total: arTotal },
      Bekasi: { byBranch: arByBranch, total: arTotal }
    }
  };

  return { compiledData, transactionsBatch };
}

/** Read branch master (MASER CABANG) into branchToArea + branches list. */
function readBranchMaster(workbook: XLSX.WorkBook): { branchToArea: Record<string, string>; branches: any[] } {
  const branchSheet = workbook.Sheets['MASER CABANG'];
  if (!branchSheet) {
    throw new Error("Lembar 'MASER CABANG' tidak ditemukan.");
  }
  const branchRange = XLSX.utils.decode_range(branchSheet['!ref'] || '');
  const branchToArea: Record<string, string> = {};
  const branches: any[] = [];

  for (let r = 2; r <= branchRange.e.r; ++r) {
    const area = getCellValue(branchSheet, r, 0);
    const code = getCellValue(branchSheet, r, 1);
    const name = getCellValue(branchSheet, r, 2);
    const openDateCell = getCellValue(branchSheet, r, 3);
    const status = getCellValue(branchSheet, r, 9);

    if (!code || !area) continue;

    const codeStr = String(code).trim();
    const areaStr = String(area).trim();
    branchToArea[codeStr] = areaStr;

    let openDate = '';
    if (openDateCell) {
      if (openDateCell instanceof Date) {
        openDate = openDateCell.toISOString().substring(0, 10);
      } else if (typeof openDateCell === 'number') {
        const d = new Date((openDateCell - 25569) * 86400 * 1000);
        openDate = d.toISOString().substring(0, 10);
      } else {
        openDate = String(openDateCell).trim();
      }
    }

    branches.push({
      code: codeStr,
      name: name ? String(name).trim() : '',
      area: areaStr,
      openDate,
      status: status ? String(status).trim() : ''
    });
  }
  return { branchToArea, branches };
}

function readMonths(workbook: XLSX.WorkBook): { MONTHS_STR: string[]; monthsMeta: any[]; monthIdxMap: Record<string, number> } {
  const plSheetName = workbook.SheetNames.find(s => s.toLowerCase() === 'p&l report' || s.toLowerCase() === 'excel profit and loss');
  if (!plSheetName) {
    throw new Error("Lembar 'P&L Report' tidak ditemukan.");
  }
  const plSheet = workbook.Sheets[plSheetName];
  const MONTHS_STR = readAnchorMonths(plSheet);
  const monthsMeta = MONTHS_STR.map(m => {
    const [y, mo] = m.split('-');
    const idMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
    const label = `${idMonths[parseInt(mo) - 1]} '${y.substring(2)}`;
    return { month: m, label, year: parseInt(y) };
  });
  const monthIdxMap: Record<string, number> = {};
  MONTHS_STR.forEach((m, idx) => { monthIdxMap[m] = idx; });
  return { MONTHS_STR, monthsMeta, monthIdxMap };
}

/**
 * Hybrid sync (preferred): GL detail is supplied as CSV text (the GL tab is too large
 * to parse from xlsx in-browser without OOM), while the small pre-aggregated sheets
 * come from the xlsx — parsed WITHOUT the GL tab so SheetJS never decompresses it.
 */
export function parseHybridSync(xlsxBuffer: ArrayBuffer, glCsvText: string, entity: string): SyncParseResult {
  const workbook = XLSX.read(new Uint8Array(xlsxBuffer), {
    type: 'array',
    // Deliberately EXCLUDE 'GL GAB' — it decompresses to ~400MB of XML and OOMs the tab.
    sheets: ['P&L Report', 'Excel Profit and loss', 'Balance Sheet', 'Excel balance Sheet', 'Cash Flow', 'Excel Cash flow', 'AR Report', 'MASER CABANG'],
    cellFormula: false,
    cellHTML: false,
    cellNF: false,
    cellStyles: false,
    cellText: false,
    cellDates: false
  });

  const { branchToArea, branches } = readBranchMaster(workbook);
  let { MONTHS_STR, monthsMeta, monthIdxMap } = readMonths(workbook);

  const glRows = parseCsvText(glCsvText);
  const cellAt = (r: number, c: number) => (glRows[r] ? (glRows[r][c] ?? null) : null);

  // Extend MONTHS_STR from GL CSV if GL contains months beyond the xlsx anchor row.
  // This ensures new months captured in GL (before pre-aggregated sheets are updated) are
  // not silently dropped. P&L numbers for GL-only months will be correct (GL-derived);
  // BS/CF for those months will be zero until the xlsx anchor row is also extended.
  const xlsxMonthSet = new Set<string>(MONTHS_STR);
  const allMonthsSet = new Set<string>(MONTHS_STR);
  for (let r = 2; r < glRows.length; r++) {
    const moVal = cellAt(r, 3);
    if (!moVal || isBlank(moVal)) continue;
    const mk = decodeMonth(moVal);
    if (mk && !xlsxMonthSet.has(mk)) allMonthsSet.add(mk);
  }

  if (allMonthsSet.size > MONTHS_STR.length) {
    const idMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
    const allSorted = Array.from(allMonthsSet).sort();
    MONTHS_STR = allSorted;
    monthsMeta = allSorted.map(m => {
      const [y, mo] = m.split('-');
      const label = `${idMonths[parseInt(mo) - 1]} '${y.substring(2)}`;
      return { month: m, label, year: parseInt(y) };
    });
    monthIdxMap = {};
    allSorted.forEach((m, idx) => { monthIdxMap[m] = idx; });
  }

  const monthsLen = MONTHS_STR.length;
  const glc = ingestGl(cellAt, glRows.length, monthIdxMap, monthsLen, branchToArea, entity, parseGlNum);
  // Release the raw CSV grid before the heavy compile/serialize phase.
  glRows.length = 0;

  return assembleCompiledData(workbook, MONTHS_STR, monthsMeta, monthsLen, branches, branchToArea, glc);
}

/**
 * Legacy single-file path: GL detail read from the xlsx 'GL GAB' tab. Kept for
 * backward compatibility; not used by the live sync because large GL tabs OOM here.
 */
export function parseExcelSync(arrayBuffer: ArrayBuffer, entity: string): SyncParseResult {
  const workbook = XLSX.read(new Uint8Array(arrayBuffer), {
    type: 'array',
    sheets: ['GL GAB', 'P&L Report', 'Excel Profit and loss', 'Balance Sheet', 'Excel balance Sheet', 'Cash Flow', 'Excel Cash flow', 'AR Report', 'MASER CABANG'],
    cellFormula: false,
    cellHTML: false,
    cellNF: false,
    cellStyles: false,
    cellText: false,
    cellDates: false
  });

  const { branchToArea, branches } = readBranchMaster(workbook);
  const { MONTHS_STR, monthsMeta, monthIdxMap } = readMonths(workbook);
  const monthsLen = MONTHS_STR.length;

  const glSheet = workbook.Sheets['GL GAB'];
  if (!glSheet) {
    throw new Error("Lembar 'GL GAB' tidak ditemukan.");
  }
  const glRange = XLSX.utils.decode_range(glSheet['!ref'] || '');
  const cellAt = (r: number, c: number) => getCellValue(glSheet, r, c);
  const glc = ingestGl(cellAt, glRange.e.r + 1, monthIdxMap, monthsLen, branchToArea, entity, (v) => Number(v || 0));

  return assembleCompiledData(workbook, MONTHS_STR, monthsMeta, monthsLen, branches, branchToArea, glc);
}
